import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { useAuth } from '@/auth/useAuth';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/Spinner';
import type { RateEstimate } from '@/types/api';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const cell = (v: number | null) => (v === null ? '' : money.format(v));

const schema = z
  .object({
    closingDate: z.string(),
    state: z.string(),
    county: z.string(),
    policyType: z.string().min(1, 'Select a policy type'),
    purchase: z.string(),
    loan: z.string(),
  })
  .superRefine((val, ctx) => {
    // Mirrors rate.js: LO needs purchase, LM needs loan, simultaneous needs both.
    const needPurchase = val.policyType === 'LO' || val.policyType === 'LM & LO';
    const needLoan = val.policyType === 'LM' || val.policyType === 'LM & LO';
    if (needPurchase && !val.purchase.trim())
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['purchase'], message: 'Purchase price is required' });
    if (needLoan && !val.loan.trim())
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['loan'], message: 'Loan amount is required' });
  });
type Values = z.infer<typeof schema>;

const toNumber = (s: string) => Number(s.replace(/[$,]/g, '')) || 0;

/** Format an ISO YYYY-MM-DD date as mm/dd/YYYY for display. */
function fmtDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

/** Today as YYYY-MM-DD (local), for the date input default. */
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function RatePage() {
  const { status, logout } = useAuth();
  const authed = status === 'authenticated';

  const { data: policyTypes = [] } = useQuery({ queryKey: ['rate-policy-types'], queryFn: () => api.getRatePolicyTypes() });
  const { data: counties = [] } = useQuery({ queryKey: ['rate-counties'], queryFn: () => api.getRateCounties() });

  const today = todayStr();
  const {
    register, handleSubmit, reset, setValue,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { closingDate: today, state: 'OK', county: 'Oklahoma', policyType: '', purchase: '', loan: '' },
  });

  // The policy-type/county options load async; re-apply the intended defaults
  // once they're present so the rendered <select> matches the form state — else
  // the value submitted can differ from what's shown (e.g. Loan shown, Loan &
  // Owner sent). Defaults to the first policy type, like the live page. Runs once.
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (defaultsApplied.current || policyTypes.length === 0 || counties.length === 0) return;
    defaultsApplied.current = true;
    setValue('policyType', policyTypes[0].name);
    setValue('county', counties.includes('Oklahoma') ? 'Oklahoma' : counties[0]);
  }, [policyTypes, counties, setValue]);

  const estimate = useMutation<RateEstimate, Error, Values>({
    mutationFn: (v) =>
      api.estimateRate({
        closingDate: v.closingDate, state: v.state, county: v.county,
        policyType: v.policyType, purchase: toNumber(v.purchase), loan: toNumber(v.loan),
      }),
  });

  const onSubmit = (v: Values) => estimate.mutate(v);
  const result = estimate.data;

  return (
    <div className="ast-rate-page">
      {/* Standalone header (no app sidebar — matches the live standalone page) */}
      <header className="ast-rate-header">
        <div className="ast-rate-logo" aria-label="American Security Title" />
        <nav className="ast-rate-nav">
          {authed ? (
            <>
              <Link className="btn btn-outline-secondary btn-sm" to="/">Home</Link>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => void logout()}>Logout</button>
            </>
          ) : (
            <Link className="btn btn-outline-secondary btn-sm" to="/login">Sign In</Link>
          )}
        </nav>
      </header>

      <main className="ast-rate-main">
        <h2 className="serif ast-page-title">Rate Calculator</h2>
        <hr />

        <form className="ast-card ast-form-section" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="ast-grid-2">
            <FormField label="Closing Date" htmlFor="rate-date">
              <input id="rate-date" type="date" className="form-control" {...register('closingDate')} />
            </FormField>
            <FormField label="State" htmlFor="rate-state">
              {/* Oklahoma only — disabled, same as the live page. */}
              <select id="rate-state" className="form-select" disabled {...register('state')}>
                <option value="OK">Oklahoma</option>
              </select>
            </FormField>
            <FormField label="Policy Type" htmlFor="rate-poltype" error={errors.policyType?.message}>
              <select id="rate-poltype" className="form-select" {...register('policyType')}>
                {policyTypes.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
              </select>
            </FormField>
            <FormField label="County" htmlFor="rate-county">
              <select id="rate-county" className="form-select" {...register('county')}>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Purchase Price" htmlFor="rate-price" error={errors.purchase?.message}>
              <input id="rate-price" className="form-control text-end" inputMode="decimal" placeholder="0.00" {...register('purchase')} />
            </FormField>
            <FormField label="Loan Amount" htmlFor="rate-loan" error={errors.loan?.message}>
              <input id="rate-loan" className="form-control text-end" inputMode="decimal" placeholder="0.00" {...register('loan')} />
            </FormField>
          </div>

          <div className="ast-rate-actions">
            <button type="button" className="btn btn-outline-secondary"
              onClick={() => {
                reset({
                  closingDate: today, state: 'OK',
                  county: counties.includes('Oklahoma') ? 'Oklahoma' : (counties[0] ?? ''),
                  policyType: policyTypes[0]?.name ?? '', purchase: '', loan: '',
                });
                estimate.reset();
              }}>
              Clear
            </button>
            <button type="submit" className="btn btn-ast-blue" disabled={estimate.isPending}>
              {estimate.isPending ? 'Calculating…' : 'Estimate'}
            </button>
          </div>
        </form>

        {/* Result */}
        {estimate.isPending && <div className="text-center py-4"><Spinner /></div>}
        {result && (
          <div className="ast-card ast-rate-result">
            <div className="ast-rate-result-head">
              <div>
                <h3 className="serif mb-1">Estimate</h3>
                <div className="text-muted small">Oklahoma State Rates</div>
                <div className="small mt-2">
                  {result.closingDate && <div>Est. Closing Date: {fmtDate(result.closingDate)}</div>}
                  <div>Purchase Price: {money.format(result.purchasePrice)}</div>
                  <div>Loan Amount: {money.format(result.loanAmount)}</div>
                </div>
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => window.print()}>
                <i className="bi bi-printer me-1" />Print
              </button>
            </div>

            <table className="table ast-table mt-3">
              <thead>
                <tr>
                  <th>Items</th>
                  <th className="text-end">Insurance Amount</th>
                  <th className="text-end">Lenders Only</th>
                  <th className="text-end">Lenders &amp; Owners</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className={r.emphasis === 'total' ? 'ast-rate-total' : r.emphasis === 'subtotal' ? 'ast-rate-subtotal' : ''}>
                    <td>{r.item}</td>
                    <td className="text-end">{cell(r.insuranceAmount)}</td>
                    <td className="text-end">{cell(r.lendersOnly)}</td>
                    <td className="text-end">{cell(r.lendersAndOwners)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {result.approximate && (
              <div className="text-muted small ast-rate-disclaimer">
                Estimate only. Mock rates are approximate — the live calculator uses the official OCA
                Oklahoma rate table on the server.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
